// Where the level nodes sit on the path artwork, for any height the artwork is drawn at.
//
// The artwork was laid out by hand at four heights (see roadmapPathImageHeight): the node
// positions are the base positions times a factor, less an offset. Between those heights the
// factor and offsets are interpolated, so the path can be drawn at any height in that range
// with the nodes still on it.
export interface RoadmapPathGeometry {
  factor: number;
  xOffset: number;
  yOffset: number;
  // How far the last path piece is cut short of the last level.
  endOffset: number;
}

const geometryAtHeight: Array<[number, RoadmapPathGeometry]> = [
  [200, { factor: 0.502, xOffset: 33, yOffset: 19, endOffset: 20 }],
  [320, { factor: 0.805, xOffset: 20, yOffset: 10, endOffset: 30 }],
  [400, { factor: 1, xOffset: 0, yOffset: 0, endOffset: 60 }],
  [480, { factor: 1.205, xOffset: -3, yOffset: -8, endOffset: 60 }],
];

export const ROADMAP_PATH_MIN_HEIGHT = geometryAtHeight[0][0];
export const ROADMAP_PATH_MAX_HEIGHT = geometryAtHeight[geometryAtHeight.length - 1][0];

const between = (from: number, to: number, share: number) => from + (to - from) * share;

/** The node geometry for artwork drawn this many pixels high (200 to 480). */
export const roadmapPathGeometry = (imageHeight: number): RoadmapPathGeometry => {
  const height = Math.min(Math.max(imageHeight, ROADMAP_PATH_MIN_HEIGHT), ROADMAP_PATH_MAX_HEIGHT);
  const upperIndex = geometryAtHeight.findIndex(([anchorHeight]) => anchorHeight >= height);
  const [upperHeight, upper] = geometryAtHeight[upperIndex];
  if (upperHeight === height || upperIndex === 0) return upper;
  const [lowerHeight, lower] = geometryAtHeight[upperIndex - 1];
  const share = (height - lowerHeight) / (upperHeight - lowerHeight);
  return {
    factor: between(lower.factor, upper.factor, share),
    xOffset: between(lower.xOffset, upper.xOffset, share),
    yOffset: between(lower.yOffset, upper.yOffset, share),
    endOffset: between(lower.endOffset, upper.endOffset, share),
  };
};

/**
 * In a short window the Play bar gives way to the level sheet, which is hidden until a level
 * is chosen: the path then takes the height left below the header (whole pixels, 200 to 480),
 * so it fills the window and grows or shrinks smoothly with it.
 */
export const roadmapFilledPathHeight = (roomForArtwork: number): number =>
  Math.floor(Math.min(Math.max(roomForArtwork, ROADMAP_PATH_MIN_HEIGHT), ROADMAP_PATH_MAX_HEIGHT));
